import { useCallback, useMemo } from 'react';
import { Copy } from '@/components/ui/lucide-memo';
import { toast } from 'sonner';
import { useKernelStore } from '@/kernel/kernelStores';
import { copyTextToClipboard } from '#lib/utils';
import type { EntityRowAction, InventoryEntityType } from '#types/types';

export const useStandardEntityRowActions = <T extends { id: string }>(
  _entityType: InventoryEntityType
): EntityRowAction<T>[] => {
  const lang = useKernelStore((state) => state.language);
  const canSeeDevOnlyDisplays = useKernelStore(
    (state) => Boolean(state.userContext.permissions?.canSeeDevOnlyDisplays)
  );

  const labels = useMemo(() => {
    if (lang === 'fr') {
      return {
        copyId: 'Copier ID',
        copied: 'ID copie',
        copyFailed: 'Copie impossible',
      };
    }
    return {
      copyId: 'Copy ID',
      copied: 'ID copied',
      copyFailed: 'Copy failed',
    };
  }, [lang]);

  const handleCopy = useCallback(
    async (entity: T) => {
      if (!entity?.id) return;
      try {
        await copyTextToClipboard(entity.id);
        toast.success(labels.copied);
      } catch {
        toast.error(labels.copyFailed);
      }
    },
    [labels.copied, labels.copyFailed]
  );

  return useMemo(
    () => [
      {
        id: 'copy-id',
        label: labels.copyId,
        icon: <Copy size={13} strokeWidth={2} />,
        onClick: handleCopy,
        visible: () => canSeeDevOnlyDisplays,
      },
    ],
    [canSeeDevOnlyDisplays, handleCopy, labels.copyId]
  );
};
